// © 2026 AttainIP Engineering
import { Fragment, useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search, ChevronRight, ChevronDown } from 'lucide-react';
import { fetchPayments } from '@/api/queries';
import { Card } from '@/components/Card';
import { PaymentDetail } from '@/components/PaymentDetail';
import { useAdminPrefs } from '@/lib/adminPrefs';

const DEBIT_TYPES = ['analysis', 'admin_deduct', 'expiry'];

const TYPE_FILTERS = [
  { value: '', label: 'All' },
  { value: 'purchase', label: 'Purchases' },
  { value: 'refund', label: 'Refunds' },
  { value: 'admin_grant', label: 'Admin Grants' },
  { value: 'signup_bonus', label: 'Signup Bonus' },
  { value: 'referral_bonus', label: 'Referral Bonus' },
  { value: 'feedback_bonus', label: 'Feedback Bonus' },
];

function formatCurrency(amountCents: number | null, currency: string | null): string {
  if (amountCents == null || !currency) return '—';
  const amount = amountCents / 100;
  return new Intl.NumberFormat('en', { style: 'currency', currency: currency.toUpperCase() }).format(amount);
}

export function PaymentsPage() {
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const { hideInternal } = useAdminPrefs();

  const { data, isLoading } = useQuery({
    queryKey: ['payments', { typeFilter, hideInternal }],
    queryFn: () => fetchPayments({
      limit: 200,
      transaction_type: typeFilter || undefined,
      hide_internal: hideInternal,
    }),
  });

  const payments = data?.data || [];
  const total = data?.total || payments.length;

  const filtered = useMemo(() => {
    if (!search.trim()) return payments;
    const q = search.toLowerCase();
    return payments.filter((tx: any) =>
      (tx.user_name || '').toLowerCase().includes(q) ||
      (tx.user_email || '').toLowerCase().includes(q) ||
      (tx.user_id || '').toLowerCase().includes(q) ||
      (tx.description || '').toLowerCase().includes(q) ||
      (tx.package_id || '').toLowerCase().includes(q) ||
      (tx.stripe_session_id || '').toLowerCase().includes(q) ||
      (tx.stripe_event_id || '').toLowerCase().includes(q)
    );
  }, [payments, search]);

  if (isLoading) return <p className="text-gray-500">Loading payments...</p>;

  return (
    <div>
      <h1 className="text-xl font-bold mb-6">Payments ({total})</h1>

      <div className="flex gap-2 mb-4">
        {TYPE_FILTERS.map(({ value, label }) => (
          <button
            key={value}
            onClick={() => setTypeFilter(value)}
            className={`px-3 py-1 rounded text-xs font-medium ${
              typeFilter === value ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="relative mb-4">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by user, description, package, or Stripe ID..."
          className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      <Card>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b text-left text-gray-500">
              <th className="pb-2 font-medium w-6"></th>
              <th className="pb-2 font-medium">User</th>
              <th className="pb-2 font-medium">Type</th>
              <th className="pb-2 font-medium text-right">Credits</th>
              <th className="pb-2 font-medium text-right">Payment</th>
              <th className="pb-2 font-medium">Package</th>
              <th className="pb-2 font-medium">Description</th>
              <th className="pb-2 font-medium">Stripe</th>
              <th className="pb-2 font-medium">Date</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((tx: any) => {
              const isOpen = !!expanded[tx.id];
              const toggle = () => setExpanded((s) => ({ ...s, [tx.id]: !s[tx.id] }));
              return (
                <Fragment key={tx.id}>
                  <tr
                    className="border-b last:border-0 hover:bg-gray-50 cursor-pointer"
                    onClick={toggle}
                  >
                    <td className="py-2 text-gray-400">
                      {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    </td>
                    <td className="py-2 text-xs">{tx.user_name || tx.user_email || tx.user_id?.slice(0, 8) + '...'}</td>
                    <td className="py-2">
                      <span className={`text-xs px-2 py-0.5 rounded ${
                        tx.transaction_type === 'purchase' ? 'bg-green-100 text-green-700' :
                        DEBIT_TYPES.includes(tx.transaction_type) ? 'bg-red-100 text-red-700' :
                        tx.transaction_type === 'admin_grant' ? 'bg-blue-100 text-blue-700' :
                        'bg-green-50 text-green-600'
                      }`}>
                        {tx.transaction_type}
                      </span>
                    </td>
                    <td className={`py-2 text-right font-mono text-xs ${DEBIT_TYPES.includes(tx.transaction_type) ? 'text-red-600' : 'text-green-600'}`}>
                      {DEBIT_TYPES.includes(tx.transaction_type) ? `-${tx.amount}` : `+${tx.amount}`}
                    </td>
                    <td className="py-2 text-right text-xs text-gray-500">{formatCurrency(tx.amount_cents, tx.currency)}</td>
                    <td className="py-2 text-xs text-gray-500">{tx.package_id || '—'}</td>
                    <td className="py-2 text-xs text-gray-400 truncate max-w-32">{tx.description || '—'}</td>
                    <td className="py-2 text-xs font-mono text-gray-400">
                      {tx.stripe_session_id ? tx.stripe_session_id.slice(0, 12) + '...' : '—'}
                    </td>
                    <td className="py-2 text-xs text-gray-400">{tx.created_at ? new Date(tx.created_at).toLocaleString() : '—'}</td>
                  </tr>
                  {isOpen && (
                    <tr className="border-b last:border-0 bg-gray-50">
                      <td></td>
                      <td colSpan={8} className="py-3 pr-4">
                        <PaymentDetail tx={tx} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
        {filtered.length === 0 && (
          <p className="text-gray-400 text-sm py-4 text-center">
            {search ? `No payments matching "${search}"` : 'No transactions found'}
          </p>
        )}
      </Card>
    </div>
  );
}
